import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserPlus, Building2, AlertCircle, Phone } from 'lucide-react';

const COUNTRY_CODES = [
  { code: '+52', label: 'MX +52' }, { code: '+1', label: 'US/CA +1' },
  { code: '+57', label: 'CO +57' }, { code: '+56', label: 'CL +56' },
  { code: '+54', label: 'AR +54' }, { code: '+55', label: 'BR +55' },
  { code: '+34', label: 'ES +34' },
];

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [showWaStep, setShowWaStep] = useState(false);
  const [waPhone, setWaPhone] = useState('');
  const [waCode, setWaCode] = useState('+52');

  useEffect(() => {
    api.get(`/auth/invite/${token}`).then(r => {
      setInvite(r.data);
      setName(r.data.name || '');
      const meta = r.data.metadata || {};
      if (meta.whatsapp_phone) setWaPhone(meta.whatsapp_phone);
      if (meta.phone_country_code) setWaCode(meta.phone_country_code);
      setLoading(false);
    }).catch(err => {
      setError(formatApiError(err.response?.data?.detail) || 'Invitación no válida');
      setLoading(false);
    });
  }, [token]);

  const handleAccept = async (e) => {
    e.preventDefault();
    setAccepting(true);
    setError('');
    try {
      const { data } = await api.post('/auth/invite/accept', { token, name, password });
      if (data.access_token) localStorage.setItem('access_token', data.access_token);
      if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
      await checkAuth();
      navigate('/tenant');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setAccepting(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
      <div className="w-8 h-8 border-2 border-[#E2E8F0] border-t-[#0F172A] rounded-full animate-spin" />
    </div>
  );

  if (error && !invite) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
      <div className="max-w-md text-center p-8 bg-white border border-[#E2E8F0] rounded-xl" data-testid="invite-error">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-[#0F172A] mb-2">Invitación no válida</h2>
        <p className="text-sm text-[#64748B] mb-4">{error}</p>
        <Button onClick={() => navigate('/login')} className="bg-[#0F172A] text-white">Ir a iniciar sesión</Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] p-4" data-testid="accept-invite-page">
      <div className="w-full max-w-md animate-fade-in-up">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-[#F72585]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserPlus className="w-7 h-7 text-[#F72585]" />
          </div>
          <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight">Te han invitado</h1>
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-xl p-6">
          <div className="flex items-center gap-3 p-3 bg-[#F8F9FA] rounded-lg mb-6">
            <Building2 className="w-5 h-5 text-[#F72585]" />
            <div>
              <p className="text-sm font-medium text-[#0F172A]">{invite?.tenant_name}</p>
              <p className="text-xs text-[#64748B]">
                {invite?.tenant_vertical?.replace(/_/g, ' ')} · Rol: {invite?.visible_role || invite?.tenant_role?.replace('tenant_', '')}
              </p>
            </div>
          </div>

          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

          <form onSubmit={handleAccept} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Email</Label>
              <Input value={invite?.email || ''} disabled className="bg-[#F8F9FA] border-[#E2E8F0] text-sm text-[#64748B]" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Tu nombre</Label>
              <Input value={name} onChange={e => setName(e.target.value)} required className="bg-white border-[#E2E8F0] text-sm" data-testid="invite-accept-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Crea tu contraseña</Label>
              <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} className="bg-white border-[#E2E8F0] text-sm" placeholder="Mínimo 8 caracteres" data-testid="invite-accept-password" />
            </div>

            {/* Optional WA step */}
            {!showWaStep ? (
              <button type="button" onClick={() => setShowWaStep(true)} className="text-xs text-[#F72585] hover:underline flex items-center gap-1">
                <Phone className="w-3 h-3" /> Configura tu WhatsApp (opcional)
              </button>
            ) : (
              <div className="space-y-2 p-3 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
                <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">WhatsApp</Label>
                <div className="flex gap-2">
                  <select value={waCode} onChange={e => setWaCode(e.target.value)}
                    className="text-xs border border-[#E2E8F0] rounded px-2 py-2 w-28" data-testid="accept-wa-code">
                    {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                  </select>
                  <Input value={waPhone} onChange={e => setWaPhone(e.target.value)} placeholder="5512345678" className="bg-white border-[#E2E8F0] text-sm" data-testid="accept-wa-phone" />
                </div>
                <button type="button" onClick={() => setShowWaStep(false)} className="text-xs text-[#94A3B8] hover:underline">Omitir</button>
              </div>
            )}

            <Button type="submit" disabled={accepting} className="w-full bg-[#F72585] text-white hover:bg-[#D81B60]" data-testid="accept-invite-btn">
              {accepting ? 'Aceptando...' : 'Aceptar invitación y crear cuenta'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
